/**
 * Share / Google-Preferred CTA Placement
 *
 * Crownpeak authors the share dropdown and the "Add as preferred on Google"
 * badge as siblings inside a shared wrapper:
 *
 *   <div class="share-and-google-cta-wrapper">
 *       <div class="ai-share-dropdown">...</div>
 *       <div class="google-preferred-bage">...</div>
 *   </div>
 *
 * On desktop the authored order (share dropdown, then Google badge) is kept
 * as-is. On mobile the two elements should swap places so the Google badge
 * renders first. This class only acts when BOTH elements are present inside
 * the wrapper - if either one is missing there is nothing to reorder.
 */
export class ShareGoogleCtaPlacement {
    private static instance: ShareGoogleCtaPlacement;

    private static readonly WRAPPER_SELECTOR = '.share-and-google-cta-wrapper';
    private static readonly SHARE_SELECTOR = '.ai-share-dropdown';
    private static readonly GOOGLE_BADGE_SELECTOR = '.google-preferred-bage';
    private static readonly MOBILE_MEDIA_QUERY = '(max-width: 768px)';

    private mediaQuery: MediaQueryList | null = null;
    private initialized = false;

    private readonly handleBreakpointChange = (): void => this.applyPlacement();

    static getInstance(): ShareGoogleCtaPlacement {
        if (!ShareGoogleCtaPlacement.instance) {
            ShareGoogleCtaPlacement.instance = new ShareGoogleCtaPlacement();
        }
        return ShareGoogleCtaPlacement.instance;
    }

    private constructor() {}

    init(): void {
        try {
            if (this.initialized) return;
            if (!this.getElements()) return;

            this.mediaQuery = window.matchMedia(ShareGoogleCtaPlacement.MOBILE_MEDIA_QUERY);
            this.applyPlacement();
            this.mediaQuery.addEventListener('change', this.handleBreakpointChange);

            this.initialized = true;
        } catch (error) {
            console.error('Error initializing ShareGoogleCtaPlacement:', error);
        }
    }

    /**
     * Resolves the wrapper and its two children, only returning a result
     * when the wrapper and BOTH the share dropdown and Google badge exist.
     */
    private getElements(): { wrapper: HTMLElement; shareDropdown: HTMLElement; googleBadge: HTMLElement } | null {
        const wrapper = document.querySelector<HTMLElement>(ShareGoogleCtaPlacement.WRAPPER_SELECTOR);
        if (!wrapper) return null;

        const shareDropdown = wrapper.querySelector<HTMLElement>(ShareGoogleCtaPlacement.SHARE_SELECTOR);
        const googleBadge = wrapper.querySelector<HTMLElement>(ShareGoogleCtaPlacement.GOOGLE_BADGE_SELECTOR);

        if (!shareDropdown || !googleBadge) return null;

        return { wrapper, shareDropdown, googleBadge };
    }

    private applyPlacement(): void {
        try {
            const elements = this.getElements();
            if (!elements) return;

            const { shareDropdown, googleBadge } = elements;
            const isMobile = this.mediaQuery?.matches
                ?? window.matchMedia(ShareGoogleCtaPlacement.MOBILE_MEDIA_QUERY).matches;

            if (isMobile) {
                // Mobile: Google-preferred badge first, then the share dropdown.
                shareDropdown.before(googleBadge);
            } else {
                // Desktop: restore the authored order - share dropdown first.
                googleBadge.before(shareDropdown);
            }
        } catch (error) {
            console.error('Error applying ShareGoogleCtaPlacement order:', error);
        }
    }

    /**
     * Removes the breakpoint listener. Exposed mainly for tests / SPA-style
     * teardown scenarios; not required for a normal full page load.
     */
    destroy(): void {
        this.mediaQuery?.removeEventListener('change', this.handleBreakpointChange);
        this.mediaQuery = null;
        this.initialized = false;
    }
}

export function initShareGoogleCtaPlacement(): void {
    ShareGoogleCtaPlacement.getInstance().init();
}
